import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type ActionHandledReturnObject,
  type AdditionalDetailsActions,
  type AdditionalDetailsData,
  AdyenCheckout,
  type AdyenCheckoutError,
  type UIElement,
  type UIElementProps,
  type CoreConfiguration,
  type PaymentAction,
} from "@adyen/adyen-web";
import { useDeviceType } from "@b2bportal/core-utilities";
import { logger } from "@hopper-b2b/datadog";

export const useAdyen = (params: {
  paymentAction: PaymentAction;
  locale: string;
  countryCode: string;
  onSuccess: (state: AdditionalDetailsData) => void;
  onError: (error: AdyenCheckoutError) => void;
}) => {
  const {
    paymentAction,
    locale,
    countryCode,
    onSuccess,
    onError: onErrorCallback,
  } = params;
  const { isDesktopAndUp } = useDeviceType();
  const adyenOptions = useMemo(
    () => ({
      challengeWindowSize: isDesktopAndUp ? "02" : "01",
      // Set to any of the following:
      // '02': ['390px', '400px'] -  The default window size
      // '01': ['250px', '400px']
      // '03': ['500px', '600px']
      // '04': ['600px', '400px']
      // '05': ['100%', '100%']
    }),
    [isDesktopAndUp]
  );
  const [isLoading, setLoading] = useState(false);

  const onActionHandled = useCallback(
    (actionHandled: ActionHandledReturnObject) => {
      if (
        actionHandled.actionDescription ===
        challengeIframeLoadedAction.actionDescription
      ) {
        setLoading(false);
      }
    },
    []
  );

  const onError = useCallback(
    (
      error: AdyenCheckoutError,
      _component?: UIElement<UIElementProps> | undefined
    ) => {
      logger().error("Adyen checkout error:", { error });
      onErrorCallback(error);
    },
    [onErrorCallback]
  );

  const onAdditionalDetails = useCallback(
    (
      state: AdditionalDetailsData,
      _component: UIElement<UIElementProps>,
      _actions: AdditionalDetailsActions
    ) => {
      onSuccess(state);
    },
    [onSuccess]
  );

  const config = useMemo<CoreConfiguration>(
    () => ({
      environment: import.meta.env.VITE_ADYEN_ENVIRONMENT,
      clientKey: import.meta.env.VITE_ADYEN_CLIENT_KEY,
      locale,
      countryCode,
      onError,
      onAdditionalDetails,
      onActionHandled,
    }),
    [countryCode, locale, onError, onAdditionalDetails, onActionHandled]
  );

  const [element, setElement] = useState<UIElement<UIElementProps>>();
  useEffect(() => {
    if (paymentAction == null) {
      return;
    }

    setLoading(true);
    AdyenCheckout(config)
      .then((adyen) => adyen.createFromAction(paymentAction, adyenOptions))
      .then((el) => {
        setElement(el);
      })
      .catch(onError);
  }, [config, adyenOptions, paymentAction, onError]);

  return useMemo(
    () => ({
      isLoading,
      element,
    }),
    [isLoading, element]
  );
};

// Defined in "@adyen/adyen-web" as typed string
const challengeIframeLoadedAction: ActionHandledReturnObject = {
  componentType: "3DS2Challenge",
  actionDescription: "3DS2 challenge iframe loaded",
};
